import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Save, Trash2, Plus, Loader2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeckBuilder = () => {
  const [cards, setCards] = useState([]);
  const [decks, setDecks] = useState([]);
  const [currentDeck, setCurrentDeck] = useState({ name: 'New Deck', cards: [] });
  const [selectedDeckId, setSelectedDeckId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [costFilter, setCostFilter] = useState('all');
  const [abilityFilter, setAbilityFilter] = useState('all');
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [cardsRes, decksRes] = await Promise.all([
        axios.get(`${API}/cards`),
        axios.get(`${API}/decks`)
      ]);
      setCards(cardsRes.data);
      setDecks(decksRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive'
      });
    }
  };

  const addCardToDeck = (card) => {
    if (currentDeck.cards.length >= 12) {
      toast({
        title: 'Deck Full',
        description: 'Your deck can only contain 12 cards',
        variant: 'destructive'
      });
      return;
    }

    // Check if card already in deck
    if (currentDeck.cards.find(c => c.id === card.id)) {
      toast({
        title: 'Duplicate Card',
        description: 'This card is already in your deck',
        variant: 'destructive'
      });
      return;
    }

    setCurrentDeck({
      ...currentDeck,
      cards: [...currentDeck.cards, card]
    });
  };

  const removeCardFromDeck = (cardId) => {
    setCurrentDeck({
      ...currentDeck,
      cards: currentDeck.cards.filter(c => c.id !== cardId)
    });
  };

  const loadDeck = async (deckId) => {
    try {
      const response = await axios.get(`${API}/decks/${deckId}`);
      setCurrentDeck({
        name: response.data.name,
        cards: response.data.cards
      });
      setSelectedDeckId(deckId);
      toast({
        title: 'Deck Loaded',
        description: `Loaded "${response.data.name}"`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load deck',
        variant: 'destructive'
      });
    }
  };

  const saveDeck = async (deckName) => {
    if (currentDeck.cards.length !== 12) {
      toast({
        title: 'Invalid Deck',
        description: 'Your deck must contain exactly 12 cards',
        variant: 'destructive'
      });
      return;
    }

    setSaving(true);
    try {
      const cardIds = currentDeck.cards.map(c => c.id);
      
      if (selectedDeckId) {
        // Update existing deck
        await axios.put(`${API}/decks/${selectedDeckId}`, {
          name: deckName,
          card_ids: cardIds
        });
        toast({
          title: 'Success',
          description: 'Deck updated successfully'
        });
      } else {
        // Create new deck
        await axios.post(`${API}/decks`, {
          name: deckName,
          card_ids: cardIds
        });
        toast({
          title: 'Success',
          description: 'Deck created successfully'
        });
      }
      
      fetchData();
      setIsSaveDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save deck',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteDeck = async (deckId) => {
    if (!window.confirm('Are you sure you want to delete this deck?')) return;

    try {
      await axios.delete(`${API}/decks/${deckId}`);
      toast({
        title: 'Success',
        description: 'Deck deleted successfully'
      });
      fetchData();
      if (selectedDeckId === deckId) {
        setCurrentDeck({ name: 'New Deck', cards: [] });
        setSelectedDeckId(null);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete deck',
        variant: 'destructive'
      });
    }
  };

  const newDeck = () => {
    setCurrentDeck({ name: 'New Deck', cards: [] });
    setSelectedDeckId(null);
  };

  const filteredCards = cards.filter(card => {
    const matchesSearch = card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         card.movie_source.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCost = costFilter === 'all' || card.cost === parseInt(costFilter);
    const matchesAbility = abilityFilter === 'all' || card.ability_type === abilityFilter;
    return matchesSearch && matchesCost && matchesAbility;
  });

  const getCostDistribution = () => {
    const dist = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
    currentDeck.cards.forEach(card => {
      dist[card.cost]++;
    });
    return dist;
  };

  const costDistribution = getCostDistribution();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900">
        <Loader2 className="h-12 w-12 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-6">
      <div className="container mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="outline" size="sm" data-testid="back-home-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Home
              </Button>
            </Link>
            <h1 className="text-4xl font-bold text-white" data-testid="deck-builder-title">Deck Builder</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Card Collection */}
          <div className="lg:col-span-2">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20" data-testid="card-collection">
              <CardHeader>
                <CardTitle className="text-white">Card Collection ({filteredCards.length})</CardTitle>
                <CardDescription className="text-gray-300">Click a card to add it to your deck</CardDescription>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <Label className="text-white">Search</Label>
                    <Input
                      placeholder="Search cards..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-white/20 border-white/30 text-white placeholder:text-gray-400"
                      data-testid="search-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Cost</Label>
                    <Select value={costFilter} onValueChange={setCostFilter}>
                      <SelectTrigger className="bg-white/20 border-white/30 text-white" data-testid="cost-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Costs</SelectItem>
                        {[0, 1, 2, 3, 4, 5, 6].map(cost => (
                          <SelectItem key={cost} value={cost.toString()}>{cost}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white">Ability</Label>
                    <Select value={abilityFilter} onValueChange={setAbilityFilter}>
                      <SelectTrigger className="bg-white/20 border-white/30 text-white" data-testid="ability-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Abilities</SelectItem>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="on_reveal">On Reveal</SelectItem>
                        <SelectItem value="ongoing">Ongoing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px] pr-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredCards.map(card => (
                      <GameCard
                        key={card.id}
                        card={card}
                        onClick={() => addCardToDeck(card)}
                        isInDeck={currentDeck.cards.some(c => c.id === card.id)}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Current Deck */}
          <div className="lg:col-span-1">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 sticky top-6" data-testid="current-deck">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-white">{currentDeck.name}</CardTitle>
                    <CardDescription className="text-gray-300">
                      {currentDeck.cards.length}/12 cards
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={newDeck} data-testid="new-deck-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => setIsSaveDialogOpen(true)}
                      disabled={currentDeck.cards.length !== 12}
                      data-testid="save-deck-btn"
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Cost Curve */}
                <div className="mt-4 space-y-2">
                  <Label className="text-white text-sm">Cost Curve</Label>
                  <div className="flex gap-1">
                    {Object.entries(costDistribution).map(([cost, count]) => (
                      <div key={cost} className="flex-1">
                        <div className="text-xs text-center text-white mb-1">{count}</div>
                        <div 
                          className="bg-blue-500 rounded transition-all"
                          style={{ height: `${count * 20}px`, minHeight: '4px' }}
                        />
                        <div className="text-xs text-center text-white mt-1">{cost}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {currentDeck.cards.length === 0 ? (
                      <div className="text-center text-gray-400 py-8">
                        Add cards to your deck
                      </div>
                    ) : (
                      currentDeck.cards.map((card, index) => (
                        <div 
                          key={`${card.id}-${index}`}
                          className="bg-white/10 rounded-lg p-3 flex items-center justify-between hover:bg-white/20 transition"
                          data-testid={`deck-card-${card.id}`}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <Badge variant="secondary" className="font-bold">{card.cost}</Badge>
                            <div className="flex-1">
                              <div className="text-white font-medium text-sm">{card.name}</div>
                              <div className="text-gray-400 text-xs">Power: {card.power}</div>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeCardFromDeck(card.id)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`remove-card-${card.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Saved Decks */}
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 mt-6" data-testid="saved-decks">
              <CardHeader>
                <CardTitle className="text-white">Saved Decks ({decks.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {decks.length === 0 ? (
                      <div className="text-center text-gray-400 py-4 text-sm">
                        No saved decks yet
                      </div>
                    ) : (
                      decks.map(deck => (
                        <div
                          key={deck.id}
                          className={`bg-white/10 rounded-lg p-3 flex items-center justify-between hover:bg-white/20 transition cursor-pointer ${
                            selectedDeckId === deck.id ? 'ring-2 ring-blue-400' : ''
                          }`}
                          onClick={() => loadDeck(deck.id)}
                          data-testid={`saved-deck-${deck.id}`}
                        >
                          <div className="text-white font-medium text-sm">{deck.name}</div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteDeck(deck.id);
                            }}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`delete-deck-${deck.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Save Deck Dialog */}
      <Dialog open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen}>
        <DialogContent className="bg-gray-900 border-white/20" data-testid="save-deck-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Save Deck</DialogTitle>
            <DialogDescription className="text-gray-400">
              Give your deck a name
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="deck-name" className="text-white">Deck Name</Label>
            <Input
              id="deck-name"
              value={currentDeck.name}
              onChange={(e) => setCurrentDeck({ ...currentDeck, name: e.target.value })}
              className="bg-white/20 border-white/30 text-white"
              data-testid="deck-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => saveDeck(currentDeck.name)} 
              disabled={saving}
              data-testid="confirm-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Game Card Component
const GameCard = ({ card, onClick, isInDeck }) => {
  const getAbilityColor = (type) => {
    switch (type) {
      case 'on_reveal': return 'bg-green-600';
      case 'ongoing': return 'bg-blue-600';
      default: return 'bg-gray-600';
    }
  };

  return (
    <div
      onClick={onClick}
      className={`relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-4 border-2 transition-all cursor-pointer ${
        isInDeck 
          ? 'border-green-500 opacity-60 cursor-not-allowed' 
          : 'border-gray-600 hover:border-blue-500 hover:scale-105'
      }`}
      data-testid={`game-card-${card.id}`}
    >
      {/* Cost Badge */}
      <div className="absolute top-2 left-2">
        <Badge className="bg-yellow-600 text-white font-bold text-lg px-3 py-1">
          {card.cost}
        </Badge>
      </div>

      {/* Power Badge */}
      <div className="absolute top-2 right-2">
        <Badge className="bg-red-600 text-white font-bold text-lg px-3 py-1">
          {card.power}
        </Badge>
      </div>

      {/* Card Image Placeholder */}
      <div className="bg-gray-700 rounded h-32 mb-3 mt-8 flex items-center justify-center">
        {card.image_url ? (
          <img src={card.image_url} alt={card.name} className="w-full h-full object-cover rounded" />
        ) : (
          <div className="text-gray-500 text-4xl font-bold">{card.name.charAt(0)}</div>
        )}
      </div>

      {/* Card Name */}
      <h3 className="text-white font-bold text-center mb-2">{card.name}</h3>

      {/* Ability Type */}
      {card.ability_type !== 'none' && (
        <Badge className={`${getAbilityColor(card.ability_type)} text-white text-xs w-full justify-center mb-2`}>
          {card.ability_type.replace('_', ' ').toUpperCase()}
        </Badge>
      )}

      {/* Ability Description */}
      <p className="text-gray-400 text-xs text-center line-clamp-2">
        {card.ability_description}
      </p>

      {/* Movie Source */}
      <p className="text-gray-500 text-xs text-center mt-2">{card.movie_source}</p>

      {isInDeck && (
        <div className="absolute inset-0 bg-green-500/20 rounded-lg flex items-center justify-center">
          <Badge className="bg-green-600 text-white">In Deck</Badge>
        </div>
      )}
    </div>
  );
};

export default DeckBuilder;